import {
  Group as ApiGroup,
  CreateGroupPayload,
  Relationship,
  UpdateGroupPayload,
} from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";
import { useListRelationships } from "./relations";
import { useUsers } from "./users";

export type Group = ApiGroup;

export interface GroupUser {
  id: string;
  user_id: string;
  name: string;
  email: string;
  /** Carried through so a member's tile is identical here and on Members. */
  picture_url: string;
  created_at: string;
  updated_at: string;
  relation?: string; // The user's relation to the group (owner, manager, member)
}

export function useGroups(
  options?: Partial<UseQueryOptions<Group[], AxiosError>>,
) {
  return useQuery<Group[], AxiosError>({
    queryKey: ["groups"],
    queryFn: async () => {
      return await orgAPI.groups.list();
    },
    ...options,
  });
}

export function useCreateGroup(
  options?: UseMutationOptions<Group, AxiosError, CreateGroupPayload>,
) {
  const queryClient = useQueryClient();
  return useMutation<Group, AxiosError, CreateGroupPayload>({
    mutationFn: async (groupData) => {
      return await orgAPI.groups.create(groupData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
    ...options,
  });
}

export function useUpdateGroup(
  groupId: string,
  options?: UseMutationOptions<Group, AxiosError, UpdateGroupPayload>,
) {
  const queryClient = useQueryClient();
  return useMutation<Group, AxiosError, UpdateGroupPayload>({
    mutationFn: async (groupData) => {
      return await orgAPI.groups.update(groupId, groupData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["group", groupId] });
    },
    ...options,
  });
}

export function useDeleteGroup(
  groupId: string,
  options?: UseMutationOptions<void, AxiosError>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError>({
    mutationFn: async () => {
      await orgAPI.groups.delete(groupId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["group", groupId] });
    },
    ...options,
  });
}

export function useGroup(
  groupId: string,
  options?: UseQueryOptions<Group, AxiosError>,
) {
  return useQuery<Group, AxiosError>({
    queryKey: ["group", groupId],
    queryFn: async () => {
      return await orgAPI.groups.show(groupId);
    },
    ...options,
  });
}

// Group users retrieved via relationships API (owner, manager, member relations)
export function useGroupUsers(
  groupId: string,
  /**
   * Whether this user may read the group's assignments. The server gates that
   * on `can_view_assignments` (owner + manager + member) — org-admin is NOT in
   * that set — so asking about a group you're not on returns 403. Pass false
   * and we don't ask at all.
   *
   * A dedicated flag rather than react-query's `enabled`: this gates the inner
   * relationships fetch, which is the request that gets refused.
   */
  { canViewMembers = true }: { canViewMembers?: boolean } = {},
) {
  // Get all relationships for this group
  const { data: relationships = [] } = useListRelationships("group", groupId, {
    enabled: Boolean(groupId) && canViewMembers,
  });

  // Get all organization users to match against relationships
  const { data: allUsers = [] } = useUsers();

  return useQuery<GroupUser[], AxiosError>({
    queryKey: ["groupUsers", groupId, relationships, allUsers],
    queryFn: async () => {
      // Filter relationships to only include organization_user subjects with group member relations
      const groupMemberRelationships = relationships.filter(
        (rel: Relationship) =>
          rel.subject_object_type === "organization_user" &&
          ["owner", "manager", "member"].includes(rel.relation),
      );

      // Map relationships to users and add relation info
      const groupUsers: GroupUser[] = groupMemberRelationships
        .map((rel: Relationship) => {
          // Find the user in allUsers by matching the subject_object_id
          const user = allUsers.find((u) => u.id === rel.subject_object_id);
          if (!user) return null;

          return {
            id: user.id,
            user_id: user.id,
            name: user.name,
            email: user.email,
            picture_url: user.picture_url,
            created_at: user.created_at,
            updated_at: user.updated_at,
            relation: rel.relation, // Add the relation type (owner, manager, member)
          } as GroupUser;
        })
        .filter(Boolean) as GroupUser[];

      return groupUsers;
    },
    enabled: Boolean(
      groupId &&
        canViewMembers &&
        relationships.length >= 0 &&
        allUsers.length >= 0,
    ),
  });
}
